import { palette } from '@/src/themes/palette'
import { useAppSelector } from '@store/hooks'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  beginNavigationFeedback,
  NavigationFeedbackState,
  stopNavigationFeedback,
  subscribeNavigationFeedback
} from './controller'

export {
  beginNavigationFeedback,
  beginRequestFeedback,
  endNavigationFeedback,
  endRequestFeedback,
  startNavigationFeedback,
  stopNavigationFeedback
} from './controller'
export type { NavigationFeedbackState } from './controller'

function isModifiedClick(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0
}

function isInternalAppLink(anchor: HTMLAnchorElement, currentOrigin: string) {
  const href = anchor.getAttribute('href')
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return false
  if (anchor.hasAttribute('download') || anchor.target === '_blank') return false

  try {
    const url = new URL(href, currentOrigin)
    return url.origin === currentOrigin
  } catch {
    return false
  }
}

function isSameDestination(url: URL, location: ReturnType<typeof useLocation>) {
  return url.pathname === location.pathname && url.search === location.search && url.hash === location.hash
}

/**
 * Instant feedback while React Router defers the next page
 * (lazy chunks + v7_startTransition keep the previous screen painted),
 * and a thin top bar while page-load GET requests are in flight.
 */
export function NavigationFeedback() {
  const location = useLocation()
  const isPatient = useAppSelector(state => state.user.user?.authable_type?.toLowerCase() === 'patient')
  const [feedback, setFeedback] = useState<NavigationFeedbackState>({
    fetching: false,
    navigating: false
  })

  useEffect(() => subscribeNavigationFeedback(setFeedback), [])

  // Clear nav tokens on route settle — in-flight GETs keep the top bar only.
  // (Link capture + guardedNavigate can both begin; clearing avoids a stuck count.)
  useEffect(() => {
    stopNavigationFeedback()
  }, [location.key])

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (isModifiedClick(event) || event.defaultPrevented) return

      const anchor = (event.target as Element | null)?.closest?.('a')
      if (!anchor || !(anchor instanceof HTMLAnchorElement)) return
      if (!isInternalAppLink(anchor, window.location.origin)) return

      const url = new URL(anchor.href, window.location.origin)
      if (isSameDestination(url, location)) return

      // Capture-phase start; leave-guard will stop this if a modal intercepts.
      beginNavigationFeedback()
    }

    const onPopState = () => beginNavigationFeedback()

    document.addEventListener('click', onClick, true)
    window.addEventListener('popstate', onPopState)

    return () => {
      document.removeEventListener('click', onClick, true)
      window.removeEventListener('popstate', onPopState)
    }
  }, [location.pathname, location.search, location.hash])

  const showBar = feedback.navigating || feedback.fetching
  if (!showBar) return null

  // Patient portal: keep the thin top bar only. The full-screen "Loading page…" dim
  // blanks Chart tabs under the sticky Chart navbar during startTransition navigations.
  const showBlockingOverlay = feedback.navigating && !isPatient

  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 z-[9999] h-1 overflow-hidden"
        style={{ background: 'rgba(167, 154, 70, 0.15)' }}
      >
        <div
          className="h-full w-1/3 animate-[nav-progress_1.1s_ease-in-out_infinite]"
          style={{
            background: `linear-gradient(90deg, transparent, ${palette.primary}, transparent)`
          }}
        />
      </div>
      {showBlockingOverlay ? (
        <div
          aria-busy="true"
          aria-live="polite"
          className="pointer-events-none fixed inset-0 z-[9998] flex items-start justify-center bg-black/5 pt-24"
          role="status"
        >
          <div className="rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-[#333] shadow-sm">
            Loading page…
          </div>
        </div>
      ) : null}
      <style>{`
        @keyframes nav-progress {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </>
  )
}
